/**
 * Helper utilities for standard color picker dialog and MS Office-style theme grid
 */

// Convert hex string to {r, g, b}
export function hexToRgb(hex) {
  const cleanHex = hex.replace(/^#/, '');
  const num = parseInt(cleanHex, 16);
  return {
    r: (num >> 16) & 255,
    g: (num >> 8) & 255,
    b: num & 255
  };
}

// Convert {r, g, b} to hex string
export function rgbToHex({ r, g, b }) {
  const clamp = (val) => Math.max(0, Math.min(255, Math.round(val)));
  return '#' + ((1 << 24) + (clamp(r) << 16) + (clamp(g) << 8) + clamp(b)).toString(16).slice(1).toUpperCase();
}

// Convert RGB to HSL (standard 0-360, 0-100%, 0-100%)
export function rgbToHsl({ r, g, b }) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s;
  const l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
      default: break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

// Convert HSL (0-360, 0-100, 0-100) to RGB
export function hslToRgb({ h, s, l }) {
  h /= 360;
  s /= 100;
  l /= 100;
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

// Generate MS Office-style tint/shade hex values
export function getTintOrShade(hex, tintFactor, shadeFactor) {
  const { r, g, b } = hexToRgb(hex);
  let newR = r;
  let newG = g;
  let newB = b;

  if (tintFactor > 0) {
    newR = Math.round(r + (255 - r) * tintFactor);
    newG = Math.round(g + (255 - g) * tintFactor);
    newB = Math.round(b + (255 - b) * tintFactor);
  } else if (shadeFactor > 0) {
    newR = Math.round(r * (1 - shadeFactor));
    newG = Math.round(g * (1 - shadeFactor));
    newB = Math.round(b * (1 - shadeFactor));
  }

  return rgbToHex({ r: newR, g: newG, b: newB });
}

// Generate the 10x6 Theme Colors grid
export function generateThemeColorsGrid() {
  const baseColors = [
    '#FFFFFF', '#000000', '#E7E6E6', '#44546A', '#5B9BD5', '#ED7D31', '#A5A5A5', '#FFC000', '#4472C4', '#70AD47'
  ];
  const grid = [];

  // Row 1: Base colors
  grid.push([...baseColors]);

  // Row 2: Tints (80% lighter for accents, 5% darker for white/gray background, 50% lighter for black)
  grid.push([
    getTintOrShade('#FFFFFF', 0, 0.05), // #F2F2F2
    getTintOrShade('#000000', 0.5, 0),  // #7F7F7F
    getTintOrShade('#E7E6E6', 0, 0.05), // #DBDBDB
    ...baseColors.slice(3).map(c => getTintOrShade(c, 0.8, 0))
  ]);

  // Row 3: Tints (60% lighter for accents, 15% darker for white/gray background, 35% lighter for black)
  grid.push([
    getTintOrShade('#FFFFFF', 0, 0.15),
    getTintOrShade('#000000', 0.35, 0),
    getTintOrShade('#E7E6E6', 0, 0.15),
    ...baseColors.slice(3).map(c => getTintOrShade(c, 0.6, 0))
  ]);

  // Row 4: Tints (40% lighter for accents, 25% darker for white/gray background, 25% lighter for black)
  grid.push([
    getTintOrShade('#FFFFFF', 0, 0.25),
    getTintOrShade('#000000', 0.25, 0),
    getTintOrShade('#E7E6E6', 0, 0.25),
    ...baseColors.slice(3).map(c => getTintOrShade(c, 0.4, 0))
  ]);

  // Row 5: Shades (25% darker for accents, 35% darker for white/gray background, 15% lighter for black)
  grid.push([
    getTintOrShade('#FFFFFF', 0, 0.35),
    getTintOrShade('#000000', 0.15, 0),
    getTintOrShade('#E7E6E6', 0, 0.35),
    ...baseColors.slice(3).map(c => getTintOrShade(c, 0, 0.25))
  ]);

  // Row 6: Shades (50% darker for accents, 50% darker for white/gray background, 5% lighter for black)
  grid.push([
    getTintOrShade('#FFFFFF', 0, 0.50),
    getTintOrShade('#000000', 0.05, 0),
    getTintOrShade('#E7E6E6', 0, 0.50),
    ...baseColors.slice(3).map(c => getTintOrShade(c, 0, 0.50))
  ]);

  return grid;
}

// Generate hexagons for the 127-color honeycomb (axial grid)
export function generateHoneycombHexagons(size = 13) {
  const hexagons = [];
  const maxRange = 6; // Radius of 6 rings around center

  for (let q = -maxRange; q <= maxRange; q++) {
    const rStart = Math.max(-maxRange, -maxRange - q);
    const rEnd = Math.min(maxRange, maxRange - q);
    for (let r = rStart; r <= rEnd; r++) {
      // Hexagonal distance from center (0,0)
      const k = (Math.abs(q) + Math.abs(r) + Math.abs(q + r)) / 2;

      // Cartesian coordinates (flat-topped hex)
      const x = size * 1.5 * q;
      const y = size * Math.sqrt(3) * (r + q / 2);

      let hexColor = '#FFFFFF';
      if (k > 0) {
        const angle = Math.atan2(y, x); // -PI to PI
        // Map angle to hue (0-360), rotate by -90 deg so Red is at bottom (+y)
        const hue = (angle * 180 / Math.PI - 90 + 360) % 360;

        // Map ring index k (1 to 6) to lightness (goes from light to dark)
        const lightnessMap = {
          1: 92,
          2: 84,
          3: 72,
          4: 58,
          5: 42,
          6: 28
        };
        const l = lightnessMap[k] || 50;
        hexColor = rgbToHex(hslToRgb({ h: hue, s: 100, l: l }));
      }

      hexagons.push({
        q,
        r,
        x,
        y,
        color: hexColor,
        k
      });
    }
  }

  return hexagons;
}
